import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { LinkedWorkRef } from '../types/surveillanceRoute';
import { OrderType } from '../types/orders';
import { buildLinkedWorkPath } from '../utils/linkedWorkPath';
import { compressSessionStorage } from '../utils/sessionStorage';
import { formAccidentNaming } from '../components/forms/AddBasicInfoAccidentForm/constants';
import { formNaming as formCommunicationNaming } from '../components/forms/NewCommunicationForm/constants';
import { formActionNaming } from '../components/forms/ActionForms/constants';
import { roidOTSelected } from '../state/actions/ui/roidOTSelectedGeneralView';

/**
 * Abre el trabajo (actuación o accidente) que atendió un aviso. Solo para
 * PÁGINAS: usa router y Redux, y la tabla de tramos se pinta también en
 * modales fuera del router (por eso le llega por prop, nunca desde aquí).
 */
export function useOpenLinkedWork(): (
  work: LinkedWorkRef,
  ot: { id?: string | null; roid?: string | null; type?: string | null },
  routeId?: string | null
) => void {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  return useCallback(
    (work, ot, routeId) => {
      const path = buildLinkedWorkPath(work, {
        routeId,
        otId: ot.id,
        otType: ot.type,
      });
      if (!path) return;

      if (work.type === 'accident' && work.accidentId) {
        // Limpia los contextos de otras entidades, como `AccidentOT.handleViewDetail`.
        sessionStorage.removeItem(formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT);
        sessionStorage.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
        // Mismo contexto de vuelta que `AccidentOT.handleViewDetail`.
        sessionStorage.setItem(
          formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT,
          compressSessionStorage(
            JSON.stringify({
              origin: 'workorder',
              parentOrderId: ot.id,
              parentRoid: ot.roid,
              parentOrderType: ot.type,
              entityRoid: work.roid,
            })
          )
        );
        dispatch(
          roidOTSelected(OrderType.Accident, work.roid ?? '', work.accidentId)
        );
      }
      navigate(path);
    },
    [dispatch, navigate]
  );
}
